
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Slide,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    List,
    ListItem,
    ListItemText,
    Button
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses'
import CloseIcon from "@mui/icons-material/Close";
import MonitorIcon from '@mui/icons-material/Monitor';
import {ActionModel} from '../../models/models';
import Config from "../../config";
import {getAppName,
        getObjectName} from "../../utils/helperRouter";
import {useParams} from "react-router-dom";
import { useNavigate } from "react-router";
import {ErrorComponent} from '../error/Error';
import {LayoutSelectorComponent} from '../layoutSelector/LayoutSelector';
import {ObjectModel,
        ObjectFactory,
        RecordModel,
        RecordFactory,
        FileModel,
        FieldModel,
        FieldMetadataModel,
        RecordTypeModel,
        RecordTypeFactory,
        ListViewModel,
        ListViewFactory,
        ListViewColumnModel,
        LayoutRelatedListModel,
        LayoutModel,
        LayoutTabModel,
        LayoutFactory,
        LayoutFieldModel,
        LayoutFieldFactory} from '../../models/models';
import {LayoutSectionComponent} from '../layoutSection/LayoutSection';
import {RecordTypeSelectorComponent} from '../recordTypeSelector/RecordTypeSelector';
import { useAuth } from "../../hooks/useAuth";
import {useSnackbar} from "notistack";
import {checkLayoutFieldRequirement,
        getFieldError} from '../../utils/fieldValidations';
/*

COMPONENT FOR DISPLAYING RECORD FROM A SELECTED TAB

*/



export const RecordCreateDialogComponent: React.FC<{
  action: ActionModel | null,
  onClose: () => void,
  fromRelatedList?: LayoutRelatedListModel
}> = ({
  action,
  onClose,
  fromRelatedList
}) => {

  //use fromRelatedList if a child record shall be created from a related list view
  //otherwise, use object api name from current url

  const styles = ContainerClasses();

  const { enqueueSnackbar } = useSnackbar();

  let {appName, objectName, recordUuid} = useParams();

  let objectApiName = getObjectName(objectName);
  if(fromRelatedList) objectApiName = fromRelatedList.ReferencedObject_sys.ApiName_sys;

  const {accessToken, onInvalidToken} = useAuth();

  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [objectMetadata, setObjectMetadata] = useState<ObjectModel | null>(null);
  const [selectedLayout, setSelectedLayout] = useState<LayoutModel | null>(null);
  const [selectedRecordType, setSelectedRecordType] = useState<RecordTypeModel | null>(null);
  const [availableLayouts, setAvailableLayouts] = useState<LayoutModel[]>([]);
  const [availableRecordTypes, setAvailableRecordTypes] = useState<RecordTypeModel[]>([]);
  const [layoutTab, setLayoutTab] = useState<LayoutTabModel | null>(null);
  const [mutableRecord, setMutableRecord] = useState<RecordModel | null>(null)

  const steps = ['Step 1: Select Record Type', 'Step 2: Record Information'];
  const [activeStep, setActiveStep] = useState<number>(0);
  const [skippedSteps, setSkippedSteps] = useState<number[]>([]);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);


  useEffect(() => {
    if(action !== null &&
       action?.Type_sys === 'standard' &&
       action?.ApiName_sys === 'Create_c'){
         setOpenDialog(true);
         retrieveData(objectApiName, null, null, accessToken);
    } else {
      setOpenDialog(false);
    }
  }, [action]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if(errorMessage){
     enqueueSnackbar(errorMessage, {
          variant: "error",
       });
    }
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps



  const selectLayout = (thisLayout: LayoutModel) => {
      retrieveData(objectApiName, thisLayout.ApiName_sys, null, accessToken);
  }

  const selectRecordType = (thisRecordType: RecordTypeModel) => {
      retrieveData(objectApiName, null, thisRecordType.ApiName_sys, accessToken);
  }


  const navigate = useNavigate();

  const retrieveData = async (objectApiName: string | null,
                              layoutApiName: string | null,
                              recordTypeApiName: string | null,
                              authToken: string) => {

    try {
      setIsFetching(true);
      setErrorMessage(null);

        //get object information for the selected lookup
      let url = `${Config.getInstance().getCoordinationServiceURL()}/api/c/${objectApiName}`;
      const headers = new Headers();

      headers.append("Authorization", `Bearer ${authToken}`);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");
      headers.append("crm-layout-api-name", `${layoutApiName}`);
      headers.append("crm-recordtype-api-name", `${recordTypeApiName}`);

      if(fromRelatedList){
//        const parentObjectApiName = getObjectName(objectName);

        headers.append("crm-related-listview-uuid", `${fromRelatedList.Uuid_sys}`)
        headers.append("crm-parent-record-uuid", `${recordUuid}`)

      }

      const response = await fetch(url, {
          method: "GET",
          headers: headers,
        })

        if(!response.ok) {
          const errorData = await response.json();
          if(!errorData.message){
            setErrorMessage("Error when fetching records");
          } else{
            setErrorMessage(errorData.message);
          }
  //        setIsFetching(false);
          return;
        }

        const data = await response.json();

        if(!data){
            setErrorMessage("Error when fetching data");
            setIsFetching(false);
            return;
        }

        if(!data.metadata ||
           !data.metadata.object ||
           !data.metadata.layout ||
           !data.metadata.layouts ||
           !data.metadata.recordtype ||
           !data.metadata.recordtypes ||
           !Array.isArray(data.metadata.layouts) ||
           !Array.isArray(data.metadata.layout.layout_tabs) ||
           !Array.isArray(data.metadata.recordtypes)){
            setErrorMessage("Error when fetching data");
            setIsFetching(false);
            return;
        }

        if(data.metadata.layout.layout_tabs.length <= 0){
            setErrorMessage("Error when fetching data");
            setIsFetching(false);
            return;
        }

        setObjectMetadata(new ObjectFactory().fromJSON(data.metadata.object))

        const thisLayouts = data.metadata.layouts.map((thisLayout: any) => new LayoutFactory().fromJSON(thisLayout))
        setAvailableLayouts(thisLayouts)

        const thisRecordType = new RecordTypeFactory().fromJSON(data.metadata.recordtype)
        setSelectedRecordType(thisRecordType);

        const thisLayout = new LayoutFactory().fromJSON(data.metadata.layout)
        setSelectedLayout(thisLayout)
        //DEBUG: FOR NOW USE TAB WITH API NAME Detail_c (need to be changed in future)
        const thisTab = thisLayout.layout_tabs.find((thisTab) => thisTab.ApiName_sys === 'Detail_c')
        if(thisTab){
          setLayoutTab(thisTab);
        }

        const thisRecordTypes = data.metadata.recordtypes.map((thisRecordType: any) => new RecordTypeFactory().fromJSON(thisRecordType))
        setAvailableRecordTypes(thisRecordTypes)

        //all fields on layout
        const fieldMetadata: FieldMetadataModel[] = [];
        const thisFields = thisLayout.layout_tabs.map((thisTab) => {
                              thisTab.layout_sections.map((thisSection) => {
                                  thisSection.layout_fields.map((thisField) => {
                                    fieldMetadata.push(thisField.ReferencedField_sys);
                                  }
                                )
                              })
        })

        //new version: record initialized in backend
        const record = new RecordFactory().fromJSON(data.record);
        setMutableRecord(record);

        //jump over record type selector because not requried
        if(availableRecordTypes.length < 1){
          setActiveStep(1)
        }

        setIsFetching(false);

    } catch (error){
       setErrorMessage("Unhandled error occurred in RecordCreateDialog, please contact your system administrator")
       setIsFetching(false);
    }

  }

  const saveData = async (appApiName: string | null,
                          objectApiName: string | null,
                          newRecord: RecordModel | null,
                          authToken: string) => {
      if(newRecord === null){
        return
      }

      const allErrors: LayoutFieldModel[] = [];
      layoutTab?.layout_sections.forEach((thisSection) => {
        //check all sections for errors
        thisSection.layout_fields.forEach((thisLayoutField) => {
          //check all fields in this section for errors
          const fieldApiName = thisLayoutField.ReferencedField_sys.ApiName_sys;
          const thisField = newRecord?.fields[fieldApiName];
          const thisError = getFieldError(thisField, thisLayoutField);
          if(thisError) allErrors.push(thisLayoutField)
        })
      })

      if(allErrors.length >= 1){
        enqueueSnackbar(
          <Box>
            <Typography>
              Error: some fields are not defined or not properly validated
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            {allErrors.map((error, index) => (
              <Typography
                key={index}
                sx={{textAlign: 'left', pl: 3}}
              >
                {error.ReferencedField_sys.Label_sys}
              </Typography>
            ))}
            </Box>
          </Box>,
          {
            variant: "error",
          }
        );
        return
      }

    try {

    setIsFetching(true);

      //get object information for the selected lookup
    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/c/${objectApiName}`;
    const headers = new Headers();

    headers.append("Authorization", `Bearer ${authToken}`);

    const requestBody = new RecordFactory().toJSON(newRecord);
    const all_files = new RecordFactory().getFILES(newRecord);

    const requestBlob = new Blob([JSON.stringify(requestBody)], {type: "application/json"});
    const formData = new FormData();
    formData.append('json', requestBlob, 'record.json')

    all_files.map((thisFile: FileModel) => {
      formData.append(thisFile.fieldName, thisFile.file)
    })


    const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: formData
      })

      if(!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          setErrorMessage("Error when saving record");
        } else{
          setErrorMessage(errorData.message);
        }
        setIsFetching(false);
        return;
      }

      const data = await response.json();

      if(!data ||
         !data.record){
          setErrorMessage("Error when saving record");
          setIsFetching(false);
          return;
      }

      setIsFetching(false);
      const recordUuid = data.record
      navigate(`/${appApiName}/${objectApiName}/${recordUuid}`);
    } catch (error){
       setErrorMessage("Unhandled error occurred in RecordCreateDialog, please contact your system administrator")
       setIsFetching(false);
    }

  }

//handle next step
const handleNext = () => {
  //allow next step if record type is defined
  if (availableRecordTypes.length !== 0 &&
      !selectedRecordType){
       setErrorMessage("Error: Please select a record Type")
  }
  else {
    setActiveStep(
      (previousActiveStep) => {
        let thisActiveStep = 0;
        activeStep === steps.length - 1 ?
        thisActiveStep = steps.length - 1 :
        thisActiveStep = previousActiveStep + 1;
        return thisActiveStep
      }
    );
}
};

//handle previous step
const handlePrevious = () => {
  setActiveStep(
      (previousActiveStep) =>
      {
        let thisActiveStep = 0;
        activeStep === 0 ?
            thisActiveStep = 0 :
            thisActiveStep = previousActiveStep - 1;
        return thisActiveStep
      }
    );
};

  return (

    <Dialog
          open={openDialog}
          onClose={onClose}
          fullWidth
          maxWidth={"lg"}
          TransitionComponent={Slide}
          TransitionProps={{direction: "up"} as any}
    >
       <Box sx={styles.containerRowB}>
          <Box></Box>
          <IconButton
             edge="start"
             color="inherit"
             aria-label="close"
             onClick={onClose}
          >
             <CloseIcon />
          </IconButton>
       </Box>

        <Stepper
           activeStep={activeStep}
           alternativeLabel
           color="primary"
        >
          {steps.map((label) => (
               <Step
                 key={label}
                 completed = {false}
               >
                 <StepLabel>
                    {label}
                 </StepLabel>
               </Step>
             ))
           }
        </Stepper>

       {activeStep == 1 &&

       <DialogTitle>
          {isFetching && <LinearProgress color="primary" />}
          {!isFetching &&
              <Box sx={styles.containerRowB}>
                 <Box sx={styles.containerColB}>
                    <MonitorIcon />
                    <Typography variant="subtitle2">
                       {objectMetadata?.Label_sys}
                    </Typography>

                    <LayoutSelectorComponent
                      selectedLayout = {selectedLayout}
                      availableLayouts = {availableLayouts}
                      selectLayout = {selectLayout}
                   />
                 </Box>
              <Box></Box>
              </Box>
          }
       </DialogTitle>
     }


       <Box>


       {!isFetching &&
         activeStep == 0 &&
          <DialogContent>

          <RecordTypeSelectorComponent
            selectedRecordType = {selectedRecordType}
            availableRecordTypes = {availableRecordTypes}
            selectRecordType = {selectRecordType}
          />

          </DialogContent>
          }


       {!isFetching &&
         activeStep == 1 &&
         mutableRecord !== null &&
          <DialogContent
            sx={{pb: 2}}
          >
          {layoutTab?.layout_sections.filter((layout_section) => { //for now, display everything
                                                              return layout_section.DetailHeading_sys === true ||
                                                                     layout_section.EditHeading_sys === true} )
                                  .map((layout_section) => {
              return (
                   <LayoutSectionComponent
                      key={layout_section.Uuid_sys}
                      layoutSection={layout_section}
                      record={mutableRecord}
                      onChange={() => {}}
                   />
                 )
               })
          }
         </DialogContent>
       }

      <DialogContent>

      <Button
         style={{ marginLeft: 20, marginTop: 5, marginBottom: 5 }}
         variant="contained"
         color="primary"
         disabled = {activeStep === 0}
         onClick={() => handlePrevious()}
       >
         Previous
       </Button>

      {!isFetching &&
       activeStep == 0 &&
       <Button
         style={{ marginLeft: 20, marginTop: 5, marginBottom: 5 }}
         variant="contained"
         color="primary"
         disabled = {!selectedRecordType}
         onClick={() => handleNext()}
       >
        Next
       </Button>
     }

       {!isFetching &&
         activeStep == 1 &&
            <Button
              color="primary"
              variant="contained"
              style={{ marginLeft: 20, marginTop: 5, marginBottom: 5 }}
              onClick={() => {
                const appApiName = getAppName(appName)
                saveData(appApiName, objectApiName, mutableRecord, accessToken)
               }
              }
            >
              Save
            </Button>
       }
          </DialogContent>

       {errorMessage !== null &&
           <ErrorComponent
              errorMessage = {errorMessage}
           />
       }

       </Box>


      </Dialog>


  );

};
